import type { AdCount } from '../../../api/types/adviewApiTypes';

import { MotorSearch } from '../../../components/HomePage/components/MotorSearch/MotorSearch';
import { MotorSearch as NewMotorSearch } from '../../../components/HomePage/features/MotorSearch/MotorSearch';
import { VerticalSearch } from '../../../components/HomePage/components/VerticalSearch/VerticalSearch';
import { VERTICAL } from '../../../helpers/verticals';

import type { ISearchPageFilter } from '../../../api/types/searchPageApiTypes';

import {
  Wrapper,
  Container,
} from '../../../components/HomePage/Layouts/HomePageSearchManager.styled';

import { FEATURE_CARS_KEYWORD_SEARCH } from '../../../utils';
import { formatNumber } from '../../../helpers/formatting';
import extractMakeOptions from '../../../features/MakeModelKeywordSearch/extractMakesFromSearchFilters';

export type HomePageSearchManagerProps = {
  domain: string;
  vertical: VERTICAL;
  lastSearchDataTrackingValue?: string;
  className?: string;
  adCount: AdCount;
  motorSearchFilters: ISearchPageFilter[];
};

const HomePageSearchManager = (props: HomePageSearchManagerProps) => {
  const {
    domain,
    vertical,
    className,
    lastSearchDataTrackingValue,
    adCount,
    motorSearchFilters,
  } = props;

  const displayVertical = (vertical: VERTICAL) => {
    switch (vertical) {
      case VERTICAL.MOTOR:
        return FEATURE_CARS_KEYWORD_SEARCH ? (
          <NewMotorSearch
            initialAdCount={adCount.carsTotalAdCount}
            makes={extractMakeOptions(motorSearchFilters)}
            priceOptions={extractPriceOptions(motorSearchFilters)}
            yearOptions={extractYearOptions(motorSearchFilters)}
            domain={domain}
          />
        ) : (
          <MotorSearch
            domain={domain}
            adCount={adCount}
            searchFilters={motorSearchFilters}
          />
        );
      case VERTICAL.MARKETPLACE:
      case VERTICAL.FARMING:
        return (
          <VerticalSearch
            baseUrl={domain}
            lastSearchDataTrackingValue={lastSearchDataTrackingValue}
            vertical={vertical}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Wrapper className={className}>
      <Container>{displayVertical(vertical)}</Container>
    </Wrapper>
  );
};

type FilterOption = {
  displayName: string;
  value: string;
  count?: string;
};

type MappedOption = {
  label: string;
  value: string | null;
};

function extractFilterOptions(
  motorSearchFilters: ISearchPageFilter[],
  filterName: 'price' | 'year',
  property: 'from' | 'to',
): MappedOption[] {
  const filter = motorSearchFilters.find(
    (filter) => filter.name === filterName,
  );
  const options = filter?.subFilters?.[0]?.values?.[0]?.[property] as
    | FilterOption[]
    | undefined;

  return (
    options?.map((option) => {
      return {
        label: option.displayName,
        value: option.value === '' ? null : option.value,
      };
    }) ?? []
  );
}

const replacePlaceholderLabelWith = (
  options: MappedOption[],
  label: string,
) => {
  return options.map((option) => {
    if (option.value === null) {
      return {
        ...option,
        label,
      };
    }
    return option;
  });
};

function extractPriceOptions(motorSearchFilters: ISearchPageFilter[]) {
  const priceOptionsFrom = extractFilterOptions(
    motorSearchFilters,
    'price',
    'from',
  ).map(formatPriceOption);
  const priceOptionsTo = extractFilterOptions(
    motorSearchFilters,
    'price',
    'to',
  ).map(formatPriceOption);

  return {
    min: replacePlaceholderLabelWith(priceOptionsFrom, 'Min Price'),
    max: replacePlaceholderLabelWith(priceOptionsTo, 'Max Price'),
  };
}

function formatPriceOption(option: MappedOption) {
  if (option.value === null) return option;
  return {
    ...option,
    label: `\u20AC${formatNumber(
      Number.parseInt(option.label.replace(',', '')),
    )}`,
  };
}

function extractYearOptions(motorSearchFilters: ISearchPageFilter[]) {
  const yearOptionsFrom = extractFilterOptions(
    motorSearchFilters,
    'year',
    'from',
  );
  const yearOptionsTo = extractFilterOptions(motorSearchFilters, 'year', 'to');

  return {
    min: replacePlaceholderLabelWith(yearOptionsFrom, 'Min Year'),
    max: replacePlaceholderLabelWith(yearOptionsTo, 'Max Year'),
  };
}

export { HomePageSearchManager };
